import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
}));

const TransferTicketModal = ({ modalOpen, open, onClose, ticketid, ticketWhatsappId }) => {
	const history = useHistory();
	const [users, setUsers] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState("");
	const [selectedQueue, setSelectedQueue] = useState('');
	const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketWhatsappId);
	const classes = useStyles();
	const { findAll: findAllQueues } = useQueues();
	const { loadingWhatsapps, whatsApps } = useWhatsApps();

	const { user: loggedInUser } = useContext(AuthContext);

	useEffect(() => {
		const loadQueues = async () => {
			const list = await findAllQueues();
			setAllQueues(list);
		}
		loadQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const isModalActive = Boolean(modalOpen || open);
		if (!isModalActive) return;

		const fetchUsers = async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/users/", {
					params: { pageNumber: 1 },
				});
				// Exclui o usuário ID 1 caso retorne
				const filteredUsers = (data.users || []).filter(u => u.id !== 1);
				setUsers(filteredUsers);
				setLoading(false);
			} catch (err) {
				setLoading(false);
				toastError(err);
			}
		};

		fetchUsers();
	}, [modalOpen, open]);

	useEffect(() => {
		setSelectedWhatsapp(ticketWhatsappId);
	}, [ticketWhatsappId, modalOpen, open]);

	const selectedUser = users.find(u => u.id === selectedUserId) || null;

	const queues = selectedUser
		? allQueues.filter(q => (selectedUser.queues || []).some(uq => uq.id === q.id))
		: [];

	const whatsappHasQueue = (whatsapp, queueId) =>
		Array.isArray(whatsapp.queues) && whatsapp.queues.some(q => q.id === queueId);

	const queueWhatsapps = selectedQueue
		? whatsApps.filter(w => whatsappHasQueue(w, selectedQueue))
		: [];

	const handleUserChange = (userId) => {
		setSelectedUserId(userId);
		const user = users.find(u => u.id === userId);
		const userHasSelectedQueue = Boolean(user) &&
			(user.queues || []).some(q => q.id === selectedQueue);
		if (!userHasSelectedQueue) {
			setSelectedQueue('');
			setSelectedWhatsapp(ticketWhatsappId);
		}
	};

	const handleQueueChange = (queueId) => {
		setSelectedQueue(queueId);
		if (!queueId) {
			setSelectedWhatsapp(ticketWhatsappId);
			return;
		}
		const matches = whatsApps.filter(w => whatsappHasQueue(w, queueId));
		if (matches.length === 1) {
			setSelectedWhatsapp(matches[0].id);
		} else if (matches.some(w => w.id === ticketWhatsappId)) {
			setSelectedWhatsapp(ticketWhatsappId);
		} else {
			setSelectedWhatsapp('');
		}
	};

	const handleClose = () => {
		onClose();
		setSelectedUserId("");
		setSelectedQueue('');
		setSelectedWhatsapp(ticketWhatsappId);
	};

	const handleSaveTicket = async e => {
		e.preventDefault();
		if (!ticketid) return;

		if (!selectedQueue) {
			toast.error("Por favor, selecione uma fila para realizar a transferência.");
			return;
		}

		setLoading(true);
		try {
			let data = {};

			if (selectedUser) {
				data.userId = selectedUser.id
			}

			if (selectedQueue && selectedQueue !== null) {
				data.queueId = selectedQueue

				if (!selectedUser) {
					data.status = 'pending';
					data.userId = null;
				}
			}

			if(selectedWhatsapp) {
				data.whatsappId = selectedWhatsapp;
			}

			if (selectedUser && selectedUser.id !== null) {

				//ALTERADO PARA APARECER O NOME DO USER QUE TRANSFERIU
				const currentUser = loggedInUser.name;

				const signMessage = "Atendimento transferido de "+ currentUser+ " para "+ selectedUser.name + ".";
				const message = {
					read: 1,
					fromMe: true,
					body: signMessage,
					transfer: true, // ADICIONADO PARA IDENTIFICAR QUE É UMA MENSAGEM DE TRANSFERÊNCIA
				};
				try {
					await api.post(`/messages/${ticketid}`, message);
				} catch (err) {
					toastError(err);
				}
			}

			await api.put(`/tickets/${ticketid}`, data);

			setLoading(false);
			history.push(`/tickets`);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<Dialog open={Boolean(modalOpen || open)} onClose={handleClose} maxWidth="lg" scroll="paper">
			<form onSubmit={handleSaveTicket}>
				<DialogTitle id="form-dialog-title">
					{i18n.t("transferTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<FormControl variant="outlined" className={classes.maxWidth} style={{ marginBottom: 20 }}>
						<InputLabel>Usuário</InputLabel>
						<Select
							value={selectedUserId}
							onChange={(e) => handleUserChange(e.target.value)}
							label="Usuário"
						>
							<MenuItem value={''}>&nbsp;</MenuItem>
							{users.map((u) => (
								<MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl variant="outlined" className={classes.maxWidth}>
						<InputLabel>{i18n.t("transferTicketModal.fieldQueueLabel")}</InputLabel>
						<Select
							value={selectedQueue}
							onChange={(e) => handleQueueChange(e.target.value)}
							label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
						>
							<MenuItem value={''}>&nbsp;</MenuItem>
							{queues.map((queue) => (
								<MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					<Can
						role={loggedInUser.profile}
						perform="ticket-options:transferWhatsapp"
						yes={() => (!loadingWhatsapps && 
							<FormControl variant="outlined" className={classes.maxWidth} style={{ marginTop: 20 }}>
								<InputLabel>{i18n.t("transferTicketModal.fieldConnectionLabel")}</InputLabel>
								<Select
									value={selectedWhatsapp}
									onChange={(e) => setSelectedWhatsapp(e.target.value)}
									label={i18n.t("transferTicketModal.fieldConnectionPlaceholder")}
								>
									{queueWhatsapps.map((whasapp) => (
										<MenuItem key={whasapp.id} value={whasapp.id}>{whasapp.name}</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading}
						variant="outlined"
						type="button"
					>
						{i18n.t("transferTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="submit"
						color="primary"
						loading={loading}
					>
						{i18n.t("transferTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default TransferTicketModal;
